/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { type ComponentPropsWithoutRef, useCallback, useEffect } from 'react';

import { SERVICE_DESK } from '@jira/platform__application-editions/src/application-key.tsx';
import { eocChangesRoute } from '@jira/router-routes__eoc-changes-routes/src/eocChangesRoute.tsx';
import { eocIncidentsRoute } from '@jira/router-routes__eoc-incidents-routes/src/eocIncidentsRoute.tsx';
import { NAVIGATION_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { FIXED_ITEM } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import { LEVEL_ONE } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { SECTION_ITEM_OPERATIONS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import {
	MENU_ID_COMMUNICATIONS_STAKEHOLDERS,
	MENU_ID_OPERATIONS,
	MENU_ID_OPS_ALERTS,
	MENU_ID_OPS_ON_CALL,
	MENU_ID_OPS_OVERVIEW,
	MENU_ID_OPS_REPORTS,
	MENU_ID_OPS_REPORTS_NEW,
	MENU_ID_OPS_SERVICES,
	MENU_ID_OPS_STATUS_PAGES,
	MENU_ID_OPS_CHANGES,
	MENU_ID_OPS_INCIDENTS,
} from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import {
	L1_MENU_ID,
	type Nav4DragAndDropMenuItemProps,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { Nav4ExpandableMenuItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { Nav4MenuLinkItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { getTestId } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useFormattedMessageAndIcon } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { useConfigurableMessageAndIconMap } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-configurable-message-and-icon-map/index.tsx';
import type { ExpandableEntryPointTrigger } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/controllers/use-expandable-entry-point-trigger/index.tsx';
import { eocGlobalAlertListRoute } from '@jira/router-routes__eoc-global-alerts-routes/src/eocGlobalAlertListRoute.tsx';
import { eocTeamAlertDetailRoute } from '@jira/router-routes__eoc-team-alerts-routes/src/eocTeamAlertDetailRoute.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import { token } from '@atlaskit/tokens';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import Lozenge from '@atlaskit/lozenge/lozenge';
import {
	ExpandableMenuItemContent,
	ExpandableMenuItemTrigger,
} from '@atlaskit/side-nav-items/expandable-menu-item';
import ServiceIcon from '@atlaskit/icon-lab/core/service';
import PhoneIcon from '@atlaskit/icon/core/phone';
import IncidentIcon from '@atlaskit/icon/core/incident';
import ChangesIcon from '@atlaskit/icon/core/changes';
import CommentIcon from '@atlaskit/icon/core/comment';
import PulseIcon from '@atlaskit/icon/core/pulse';
import GraphBarIcon from '@atlaskit/icon/core/chart-bar';
import OverviewIcon from '@atlaskit/icon/core/align-text-left';
import AlertIcon from '@atlaskit/icon/core/alert';
import { mergeRefs } from '@atlassian/jira-merge-refs';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { useFeatures } from '../../controllers/features/index.tsx';
import { ChangesContent } from '../changes-content/ChangesContent.tsx';
import { IncidentsContent } from '../incidents-content/IncidentsContent.tsx';
import { ActionButtons, PopoutToggleButton } from './action-buttons/index.tsx';
import { useAlertsPopoutStore } from './action-buttons/useAlertsPopoutStore.tsx';

const homeMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.home',
	defaultMessage: 'Home',
	description: 'Assets dropdown item to link customer to Operations Home page.',
});

const onCallSchedulesMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.on-call-schedules',
	defaultMessage: 'On-call schedules',
	description: 'Assets dropdown item to link customer to Operations On-call Schedules page.',
});

const servicesMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.services',
	defaultMessage: 'Services',
	description: 'Used as the text for a menu item that when used navigates to service list page',
});

const reportsMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.reports',

	defaultMessage: 'Reports',
	description:
		'Menu item label displayed in the operations sidebar navigation. When clicked, takes user to the reports section.',
});

const reportsLegacyMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.reports-legacy',

	defaultMessage: 'Reports (Legacy)',
	description:
		'Menu item label for the legacy reports experience in the operations sidebar navigation. Shown alongside the new Reports entry during the coexistence period when platform_avp_jira_ops_reporting is enabled.',
});

const reportsNewBadgeMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.reports-new-badge',
	defaultMessage: 'New',
	description: 'Badge label shown on the new Operations Reports navigation item.',
});

const statusPagesMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.statuspages',
	defaultMessage: 'Status pages',
	description:
		'Menu item label displayed in the operations sidebar navigation. When clicked, takes user to the status pages management section.',
});

const communicationsMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.communications',
	defaultMessage: 'Communications',
	description:
		'Menu item label displayed in the operations sidebar navigation. When clicked, takes user to the communications management section.',
});

const changesMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.changes',
	defaultMessage: 'Changes',
	description:
		'Menu item label displayed in the operations sidebar navigation. When clicked, takes user to the changes management section.',
});

const incidentsMenuMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-operations.incidents',
	defaultMessage: 'Incidents',
	description:
		'Menu item label displayed in the operations sidebar navigation. When clicked, takes user to the incidents management section.',
});

const operationsMessages = {
	home: homeMenuMessage,
	onCallSchedules: onCallSchedulesMenuMessage,
	services: servicesMenuMessage,
	reports: reportsMenuMessage,
	reportsLegacy: reportsLegacyMenuMessage,
	reportsNew: reportsMenuMessage,
	reportsNewBadge: reportsNewBadgeMessage,
	statusPages: statusPagesMenuMessage,
	communications: communicationsMenuMessage,
	changes: changesMenuMessage,
	incidents: incidentsMenuMessage,
};

const alertsMessageDefinition = defineMessage({
	id: 'navigation-apps-sidebar-nav4-operations.alerts',
	defaultMessage: 'Alerts',
	description: 'Assets dropdown item to link customer to Operations Alerts page.',
});

export type OperationsMenuDefaultProps = { isExpanded: boolean };

type Props = Nav4DragAndDropMenuItemProps & {
	AlertsMenuItemContent: React.ReactNode;
	isExpanded: boolean;
	onSystemToggle?: (isExpanded: boolean) => void;
	onExpansionToggle?: (isExpanded: boolean) => void;
	triggerRef?: ExpandableEntryPointTrigger;
};

export const OperationsExpandableMenuItem: React.FC<Props> = componentWithFG(
	'navx-global-side-nav',
	OperationsExpandableMenuItemGSN,
	OperationsExpandableMenuItemNav4,
);

function OperationsExpandableMenuItemGSN(props: Props): React.JSX.Element {
	const {
		AlertsMenuItemContent,
		draggableRef,
		dropIndicator,
		hasDragIndicator,
		isDragging,
		isExpanded,
		onExpandFlyoutToggle,
		onExpansionToggle,
		onSystemToggle,
		triggerRef,
		visualContentRef,
	} = props;
	const isAlertsRoute = useIsActiveRoute(
		new Set([eocGlobalAlertListRoute.name, eocTeamAlertDetailRoute.name]),
	);
	const isChangesRoute = useIsActiveRoute(new Set([eocChangesRoute.name]));
	const isIncidentsRoute = useIsActiveRoute(new Set([eocIncidentsRoute.name]));
	const {
		hasReports,
		isOperationReportsEnabled,
		showCommunicationsMenu,
		showStatuspageMenu,
		isStatusPageDraftOnly,
	} = useFeatures();
	const { icon: alertsIcon, formattedMessage: alertsMessage } = useFormattedMessageAndIcon(
		alertsMessageDefinition,
		AlertIcon,
	);
	const { icon: homeIcon, formattedMessage: homeMessage } = useFormattedMessageAndIcon(
		operationsMessages.home,
		OverviewIcon,
	);
	const { icon: oncallSchedulesIcon, formattedMessage: oncallSchedulesMessage } =
		useFormattedMessageAndIcon(operationsMessages.onCallSchedules, PhoneIcon);
	const { icon: servicesIcon, formattedMessage: servicesMessage } = useFormattedMessageAndIcon(
		operationsMessages.services,
		ServiceIcon,
	);
	const { icon: reportsIcon, formattedMessage: reportsMessage } = useFormattedMessageAndIcon(
		operationsMessages.reports,
		GraphBarIcon,
	);
	const { icon: reportsNewIcon, formattedMessage: reportsNewMessage } = useFormattedMessageAndIcon(
		operationsMessages.reportsNew,
		GraphBarIcon,
	);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.OPERATIONS);
	const { icon: statusPageIcon, formattedMessage: statusPageMessage } = useFormattedMessageAndIcon(
		operationsMessages.statusPages,
		PulseIcon,
	);
	const { icon: communicationsIcon, formattedMessage: communicationsMessage } =
		useFormattedMessageAndIcon(operationsMessages.communications, CommentIcon);
	const { icon: changesIcon, formattedMessage: changesMessage } = useFormattedMessageAndIcon(
		operationsMessages.changes,
		ChangesIcon,
	);
	const { icon: incidentsIcon, formattedMessage: incidentsMessage } = useFormattedMessageAndIcon(
		operationsMessages.incidents,
		IncidentIcon,
	);
	const { formatMessage } = useIntl();
	const {
		state: { lastVisitedSearch },
		syncLastVisitedSearch,
	} = useAlertsPopoutStore();

	// This state is lifted from the ActionsButtons component, as we don't want to show the "..."
	// menu when it will contain no content.
	const tenantContext = useTenantContext();
	const { isOpsAdmin } = useOgTenantInfo();

	const shouldShowGeneralConfiguration =
		(tenantContext.isAdmin || isOpsAdmin) && tenantContext.licensedProducts[SERVICE_DESK];

	useEffect(() => {
		syncLastVisitedSearch();
	}, [syncLastVisitedSearch]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const onClickAlerts = useCallback<
		NonNullable<ComponentPropsWithoutRef<typeof ExpandableMenuItemTrigger>['onClick']>
	>(
		(_e, _a, { isExpanded: hasExpanded }) => {
			if (hasExpanded) {
				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
					actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
					attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'alerts' },
				});
			}
		},
		[createAnalyticsEvent],
	);

	const alertsChildren = (
		<>
			{lastVisitedSearch ? (
				<Nav4MenuLinkItem
					actions={<PopoutToggleButton />}
					elemBefore={alertsIcon}
					href={lastVisitedSearch}
					menuId={fg('ops-alerts-popout-selection-fix') ? MENU_ID_OPS_ALERTS : MENU_ID_OPS_OVERVIEW}
				>
					{alertsMessage}
				</Nav4MenuLinkItem>
			) : (
				<ExpandableMenuItemTrigger
					interactionName="nav4-sidebar-operations-alerts"
					actions={isAlertsRoute ? <PopoutToggleButton /> : undefined}
					onClick={onClickAlerts}
					elemBefore={alertsIcon}
					testId={getTestId(MENU_ID_OPS_ALERTS)}
				>
					{alertsMessage}
				</ExpandableMenuItemTrigger>
			)}
			{!lastVisitedSearch && (
				<ExpandableMenuItemContent>{AlertsMenuItemContent}</ExpandableMenuItemContent>
			)}
		</>
	);

	const handleOnExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.OPERATIONS,
					sectionItem: SECTION_ITEM_OPERATIONS,
				});
			}

			onExpansionToggle?.(hasExpanded);
			onExpandFlyoutToggle?.(hasExpanded);
		},
		[onExpandFlyoutToggle, onExpansionToggle, sendSectionItemAnalyticsEvent],
	);

	const onClickOverview = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'overview' },
		});
	}, [createAnalyticsEvent]);

	const onClickServices = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'services' },
		});
	}, [createAnalyticsEvent]);

	const onClickOnCallSchedules = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'onCallSchedules' },
		});
	}, [createAnalyticsEvent]);

	const onClickStatusPages = useCallback(() => {
		// Standard sidebar nav event (fired for every tenant that can see the item —
		// enterprise + premium experiment). The isStatusPageDraftOnly attribute lets
		// analytics distinguish the premium-experiment (draft-only) cohort.
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: {
				componentType: FIXED_ITEM,
				level: LEVEL_ONE,
				itemId: 'statusPages',
				isStatusPageDraftOnly,
			},
		});
	}, [createAnalyticsEvent, isStatusPageDraftOnly]);

	const onClickReports = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'reports' },
		});
	}, [createAnalyticsEvent]);

	const onClickReportsNew = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'reportsNew' },
		});
	}, [createAnalyticsEvent]);

	const onClickChanges = useCallback<
		NonNullable<ComponentPropsWithoutRef<typeof ExpandableMenuItemTrigger>['onClick']>
	>(
		(_e, _a, { isExpanded: hasExpanded }) => {
			if (hasExpanded) {
				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
					actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
					attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'changes' },
				});
			}
		},
		[createAnalyticsEvent],
	);

	const onClickIncidents = useCallback<
		NonNullable<ComponentPropsWithoutRef<typeof ExpandableMenuItemTrigger>['onClick']>
	>(
		(_e, _a, { isExpanded: hasExpanded }) => {
			if (hasExpanded) {
				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
					actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
					attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'incidents' },
				});
			}
		},
		[createAnalyticsEvent],
	);

	const COMMUNICATIONS_STAKEHOLDERS_HREF = '/jira/communications/stakeholders';

	return (
		<Nav4ExpandableMenuItem
			dropTargetRef={visualContentRef}
			dropIndicator={dropIndicator}
			isDragging={isDragging}
			isExpanded={isExpanded}
			menuId={MENU_ID_OPERATIONS}
			onExpansionToggle={handleOnExpansionToggle}
			onSystemToggle={onSystemToggle}
		>
			<ExpandableMenuItemTrigger
				actionsOnHover={shouldShowGeneralConfiguration && <ActionButtons />}
				elemBefore={icon}
				hasDragIndicator={hasDragIndicator}
				interactionName="nav4-sidebar-operations"
				isDragging={isDragging}
				// oxlint-disable-next-line typescript/no-non-null-assertion
				ref={mergeRefs(triggerRef!, draggableRef!)}
				testId={getTestId(MENU_ID_OPERATIONS)}
			>
				{fg('ops-overview-disc-spotlights') ? (
					<div id="operations-overview-discoverability-spotlight-target">{formattedMessage}</div>
				) : (
					formattedMessage
				)}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<Nav4MenuLinkItem
					elemBefore={homeIcon}
					href="/jira/ops/overview"
					menuId={MENU_ID_OPS_OVERVIEW}
					onClick={onClickOverview}
				>
					{homeMessage}
				</Nav4MenuLinkItem>

				<Nav4ExpandableMenuItem menuId={MENU_ID_OPS_ALERTS}>
					{alertsChildren}
				</Nav4ExpandableMenuItem>

				{expVal('changes-and-incidents-under-operations', 'isEnabled', false) && (
					<Nav4ExpandableMenuItem menuId={MENU_ID_OPS_CHANGES}>
						<ExpandableMenuItemTrigger
							interactionName="nav4-sidebar-operations-changes"
							onClick={onClickChanges}
							elemBefore={changesIcon}
							href="/jira/ops/changes"
							isSelected={isChangesRoute}
							testId={getTestId(MENU_ID_OPS_CHANGES)}
						>
							{changesMessage}
						</ExpandableMenuItemTrigger>
						<ChangesContent />
					</Nav4ExpandableMenuItem>
				)}

				{expVal('changes-and-incidents-under-operations', 'isEnabled', false) && (
					<Nav4ExpandableMenuItem menuId={MENU_ID_OPS_INCIDENTS}>
						<ExpandableMenuItemTrigger
							interactionName="nav4-sidebar-operations-incidents"
							onClick={onClickIncidents}
							elemBefore={incidentsIcon}
							href="/jira/ops/incidents"
							isSelected={isIncidentsRoute}
							testId={getTestId(MENU_ID_OPS_INCIDENTS)}
						>
							{incidentsMessage}
						</ExpandableMenuItemTrigger>
						<IncidentsContent />
					</Nav4ExpandableMenuItem>
				)}

				{fg('service-list-sidebar-item') && (
					<Nav4MenuLinkItem
						elemBefore={servicesIcon}
						href="/jira/ops/services"
						menuId={MENU_ID_OPS_SERVICES}
						onClick={onClickServices}
					>
						{servicesMessage}
					</Nav4MenuLinkItem>
				)}

				<Nav4MenuLinkItem
					elemBefore={oncallSchedulesIcon}
					href="/jira/ops/who-is-on-call"
					menuId={MENU_ID_OPS_ON_CALL}
					onClick={onClickOnCallSchedules}
				>
					{oncallSchedulesMessage}
				</Nav4MenuLinkItem>
				{showStatuspageMenu && (
					<Nav4MenuLinkItem
						elemBefore={statusPageIcon}
						{...(isStatusPageDraftOnly
							? {
									elemAfter: (
										<EditionsIcon
											label=""
											color={token('color.icon.accent.purple')}
											spacing="none"
											testId="sidebar-nav4-sidebars-content-operations.ui.operations-expandable-menu-item.premium-diamond"
										/>
									),
								}
							: {})}
						href="/jira/communications/status-page"
						menuId={MENU_ID_OPS_STATUS_PAGES}
						onClick={onClickStatusPages}
					>
						{fg('communications_status_page_manage_spotlight') ? (
							<div id="statuspage-navigation-spotlight-target">{statusPageMessage}</div>
						) : (
							statusPageMessage
						)}
					</Nav4MenuLinkItem>
				)}
				{showCommunicationsMenu && (
					<Nav4MenuLinkItem
						elemBefore={communicationsIcon}
						href={COMMUNICATIONS_STAKEHOLDERS_HREF}
						menuId={MENU_ID_COMMUNICATIONS_STAKEHOLDERS}
					>
						{communicationsMessage}
					</Nav4MenuLinkItem>
				)}
				{hasReports && (
					<Nav4MenuLinkItem
						elemBefore={reportsIcon}
						href="/jira/ops/reports"
						menuId={MENU_ID_OPS_REPORTS}
						onClick={onClickReports}
					>
						{fg('platform_avp_jira_ops_reporting')
							? formatMessage(operationsMessages.reportsLegacy)
							: reportsMessage}
					</Nav4MenuLinkItem>
				)}
				{isOperationReportsEnabled && fg('platform_avp_jira_ops_reporting') && (
					<Nav4MenuLinkItem
						elemBefore={reportsNewIcon}
						href="/jira/ops/reports-new"
						menuId={MENU_ID_OPS_REPORTS_NEW}
						onClick={onClickReportsNew}
						elemAfter={
							<Lozenge appearance="discovery">
								{formatMessage(operationsMessages.reportsNewBadge)}
							</Lozenge>
						}
					>
						{reportsNewMessage}
					</Nav4MenuLinkItem>
				)}
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}

function OperationsExpandableMenuItemNav4(props: Props): React.JSX.Element {
	const isAlertsRoute = useIsActiveRoute(
		new Set([eocGlobalAlertListRoute.name, eocTeamAlertDetailRoute.name]),
	);
	const isChangesRoute = useIsActiveRoute(new Set([eocChangesRoute.name]));
	const isIncidentsRoute = useIsActiveRoute(new Set([eocIncidentsRoute.name]));
	const { AlertsMenuItemContent, isExpanded, onExpansionToggle, onSystemToggle, triggerRef } =
		props;
	const {
		hasReports,
		isOperationReportsEnabled,
		showCommunicationsMenu,
		showStatuspageMenu,
		isStatusPageDraftOnly,
	} = useFeatures();
	const { icon: alertsIcon, formattedMessage: alertsMessage } = useFormattedMessageAndIcon(
		alertsMessageDefinition,
		AlertIcon,
	);
	const { icon: homeIcon, formattedMessage: homeMessage } = useFormattedMessageAndIcon(
		operationsMessages.home,
		OverviewIcon,
	);
	const { icon: oncallSchedulesIcon, formattedMessage: oncallSchedulesMessage } =
		useFormattedMessageAndIcon(operationsMessages.onCallSchedules, PhoneIcon);
	const { icon: servicesIcon, formattedMessage: servicesMessage } = useFormattedMessageAndIcon(
		operationsMessages.services,
		ServiceIcon,
	);
	const { icon: reportsIcon, formattedMessage: reportsMessage } = useFormattedMessageAndIcon(
		operationsMessages.reports,
		GraphBarIcon,
	);
	const { icon: reportsNewIcon, formattedMessage: reportsNewMessage } = useFormattedMessageAndIcon(
		operationsMessages.reportsNew,
		GraphBarIcon,
	);
	const { formattedMessage, icon } = useConfigurableMessageAndIconMap(L1_MENU_ID.OPERATIONS);
	const { icon: statusPageIcon, formattedMessage: statusPageMessage } = useFormattedMessageAndIcon(
		operationsMessages.statusPages,
		PulseIcon,
	);
	const { icon: communicationsIcon, formattedMessage: communicationsMessage } =
		useFormattedMessageAndIcon(operationsMessages.communications, CommentIcon);
	const { icon: changesIcon, formattedMessage: changesMessage } = useFormattedMessageAndIcon(
		operationsMessages.changes,
		ChangesIcon,
	);
	const { icon: incidentsIcon, formattedMessage: incidentsMessage } = useFormattedMessageAndIcon(
		operationsMessages.incidents,
		IncidentIcon,
	);
	const { formatMessage } = useIntl();
	const {
		state: { lastVisitedSearch },
		syncLastVisitedSearch,
	} = useAlertsPopoutStore();

	useEffect(() => {
		syncLastVisitedSearch();
	}, [syncLastVisitedSearch]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();

	const onClickAlerts = useCallback<
		NonNullable<ComponentPropsWithoutRef<typeof ExpandableMenuItemTrigger>['onClick']>
	>(
		(_e, _a, { isExpanded: hasExpanded }) => {
			if (hasExpanded) {
				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
					actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
					attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'alerts' },
				});
			}
		},
		[createAnalyticsEvent],
	);

	const alertsChildren = (
		<>
			{lastVisitedSearch ? (
				<Nav4MenuLinkItem
					actions={<PopoutToggleButton />}
					elemBefore={alertsIcon}
					href={lastVisitedSearch}
					menuId={fg('ops-alerts-popout-selection-fix') ? MENU_ID_OPS_ALERTS : MENU_ID_OPS_OVERVIEW}
				>
					{alertsMessage}
				</Nav4MenuLinkItem>
			) : (
				<ExpandableMenuItemTrigger
					interactionName="nav4-sidebar-operations-alerts"
					actions={isAlertsRoute ? <PopoutToggleButton /> : undefined}
					onClick={onClickAlerts}
					elemBefore={alertsIcon}
					testId={getTestId(MENU_ID_OPS_ALERTS)}
				>
					{alertsMessage}
				</ExpandableMenuItemTrigger>
			)}
			{!lastVisitedSearch && (
				<ExpandableMenuItemContent>{AlertsMenuItemContent}</ExpandableMenuItemContent>
			)}
		</>
	);

	const handleOnExpansionToggle = useCallback(
		(hasExpanded: boolean) => {
			if (hasExpanded) {
				sendSectionItemAnalyticsEvent({
					menuId: L1_MENU_ID.OPERATIONS,
					sectionItem: SECTION_ITEM_OPERATIONS,
				});
			}

			onExpansionToggle?.(hasExpanded);
		},
		[onExpansionToggle, sendSectionItemAnalyticsEvent],
	);

	const onClickOverview = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'overview' },
		});
	}, [createAnalyticsEvent]);

	const onClickServices = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'services' },
		});
	}, [createAnalyticsEvent]);

	const onClickOnCallSchedules = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'onCallSchedules' },
		});
	}, [createAnalyticsEvent]);

	const onClickStatusPages = useCallback(() => {
		// Standard sidebar nav event (fired for every tenant that can see the item —
		// enterprise + premium experiment). The isStatusPageDraftOnly attribute lets
		// analytics distinguish the premium-experiment (draft-only) cohort.
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: {
				componentType: FIXED_ITEM,
				level: LEVEL_ONE,
				itemId: 'statusPages',
				isStatusPageDraftOnly,
			},
		});
	}, [createAnalyticsEvent, isStatusPageDraftOnly]);

	const onClickReports = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'reports' },
		});
	}, [createAnalyticsEvent]);

	const onClickReportsNew = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), {
			action: 'clicked',
			actionSubject: NAVIGATION_ITEM,
			actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
			attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'reportsNew' },
		});
	}, [createAnalyticsEvent]);

	const onClickChanges = useCallback<
		NonNullable<ComponentPropsWithoutRef<typeof ExpandableMenuItemTrigger>['onClick']>
	>(
		(_e, _a, { isExpanded: hasExpanded }) => {
			if (hasExpanded) {
				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
					actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
					attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'changes' },
				});
			}
		},
		[createAnalyticsEvent],
	);

	const onClickIncidents = useCallback<
		NonNullable<ComponentPropsWithoutRef<typeof ExpandableMenuItemTrigger>['onClick']>
	>(
		(_e, _a, { isExpanded: hasExpanded }) => {
			if (hasExpanded) {
				fireUIAnalytics(createAnalyticsEvent({}), {
					action: 'clicked',
					actionSubject: NAVIGATION_ITEM,
					actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
					attributes: { componentType: FIXED_ITEM, level: LEVEL_ONE, itemId: 'incidents' },
				});
			}
		},
		[createAnalyticsEvent],
	);

	const COMMUNICATIONS_STAKEHOLDERS_HREF = '/jira/communications/stakeholders';

	return (
		<Nav4ExpandableMenuItem
			isExpanded={isExpanded}
			menuId={MENU_ID_OPERATIONS}
			onExpansionToggle={handleOnExpansionToggle}
			onSystemToggle={onSystemToggle}
		>
			<ExpandableMenuItemTrigger
				interactionName="nav4-sidebar-operations"
				actionsOnHover={<ActionButtons />}
				elemBefore={icon}
				ref={triggerRef}
				testId={getTestId(MENU_ID_OPERATIONS)}
			>
				{fg('ops-overview-disc-spotlights') ? (
					<div id="operations-overview-discoverability-spotlight-target">{formattedMessage}</div>
				) : (
					formattedMessage
				)}
			</ExpandableMenuItemTrigger>
			<ExpandableMenuItemContent>
				<Nav4MenuLinkItem
					elemBefore={homeIcon}
					href="/jira/ops/overview"
					menuId={MENU_ID_OPS_OVERVIEW}
					onClick={onClickOverview}
				>
					{homeMessage}
				</Nav4MenuLinkItem>

				<Nav4ExpandableMenuItem menuId={MENU_ID_OPS_ALERTS}>
					{alertsChildren}
				</Nav4ExpandableMenuItem>

				{expVal('changes-and-incidents-under-operations', 'isEnabled', false) && (
					<Nav4ExpandableMenuItem menuId={MENU_ID_OPS_CHANGES}>
						<ExpandableMenuItemTrigger
							interactionName="nav4-sidebar-operations-changes"
							onClick={onClickChanges}
							elemBefore={changesIcon}
							href="/jira/ops/changes"
							isSelected={isChangesRoute}
							testId={getTestId(MENU_ID_OPS_CHANGES)}
						>
							{changesMessage}
						</ExpandableMenuItemTrigger>
						<ChangesContent />
					</Nav4ExpandableMenuItem>
				)}

				{expVal('changes-and-incidents-under-operations', 'isEnabled', false) && (
					<Nav4ExpandableMenuItem menuId={MENU_ID_OPS_INCIDENTS}>
						<ExpandableMenuItemTrigger
							interactionName="nav4-sidebar-operations-incidents"
							onClick={onClickIncidents}
							elemBefore={incidentsIcon}
							href="/jira/ops/incidents"
							isSelected={isIncidentsRoute}
							testId={getTestId(MENU_ID_OPS_INCIDENTS)}
						>
							{incidentsMessage}
						</ExpandableMenuItemTrigger>
						<IncidentsContent />
					</Nav4ExpandableMenuItem>
				)}

				{fg('service-list-sidebar-item') && (
					<Nav4MenuLinkItem
						elemBefore={servicesIcon}
						href="/jira/ops/services"
						menuId={MENU_ID_OPS_SERVICES}
						onClick={onClickServices}
					>
						{servicesMessage}
					</Nav4MenuLinkItem>
				)}

				<Nav4MenuLinkItem
					elemBefore={oncallSchedulesIcon}
					href="/jira/ops/who-is-on-call"
					menuId={MENU_ID_OPS_ON_CALL}
					onClick={onClickOnCallSchedules}
				>
					{oncallSchedulesMessage}
				</Nav4MenuLinkItem>
				{showStatuspageMenu && (
					<Nav4MenuLinkItem
						elemBefore={statusPageIcon}
						{...(isStatusPageDraftOnly
							? {
									elemAfter: (
										<EditionsIcon
											label=""
											color={token('color.icon.accent.purple')}
											spacing="none"
											testId="sidebar-nav4-sidebars-content-operations.ui.operations-expandable-menu-item.premium-diamond"
										/>
									),
								}
							: {})}
						href="/jira/communications/status-page"
						menuId={MENU_ID_OPS_STATUS_PAGES}
						onClick={onClickStatusPages}
					>
						{fg('communications_status_page_manage_spotlight') ? (
							<div id="statuspage-navigation-spotlight-target">{statusPageMessage}</div>
						) : (
							statusPageMessage
						)}
					</Nav4MenuLinkItem>
				)}
				{showCommunicationsMenu && (
					<Nav4MenuLinkItem
						elemBefore={communicationsIcon}
						href={COMMUNICATIONS_STAKEHOLDERS_HREF}
						menuId={MENU_ID_COMMUNICATIONS_STAKEHOLDERS}
					>
						{communicationsMessage}
					</Nav4MenuLinkItem>
				)}
				{hasReports && (
					<Nav4MenuLinkItem
						elemBefore={reportsIcon}
						href="/jira/ops/reports"
						menuId={MENU_ID_OPS_REPORTS}
						onClick={onClickReports}
					>
						{fg('platform_avp_jira_ops_reporting')
							? formatMessage(operationsMessages.reportsLegacy)
							: reportsMessage}
					</Nav4MenuLinkItem>
				)}
				{isOperationReportsEnabled && fg('platform_avp_jira_ops_reporting') && (
					<Nav4MenuLinkItem
						elemBefore={reportsNewIcon}
						href="/jira/ops/reports-new"
						menuId={MENU_ID_OPS_REPORTS_NEW}
						onClick={onClickReportsNew}
						elemAfter={
							<Lozenge appearance="discovery">
								{formatMessage(operationsMessages.reportsNewBadge)}
							</Lozenge>
						}
					>
						{reportsNewMessage}
					</Nav4MenuLinkItem>
				)}
			</ExpandableMenuItemContent>
		</Nav4ExpandableMenuItem>
	);
}
